import { useFormik } from "formik";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { SIGNIN_STORE_USER } from "src/constants/mutations";
import * as Yup from "yup";
import eyeIconVisible from "../../assests/images/landing-page/eye-open.png";
import eyeIcon from "../../assests/images/landing-page/visibility_off.png";
import Logo from "../../components/common/Logo";
import { useAuth } from "../../contexts/auth-provider";
import { setCachedFeatureNames } from "src/utils/isAllowedFeature";
import { BtnLoader } from "../../Loader/index";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";

const MailIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

export const Login = () => {
  const INVALID_CREDENTIALS_MESSAGE = "Invalid email or password";
  const GENERIC_LOGIN_MESSAGE = "Unable to sign in right now. Please try again.";
  const navigate = useNavigate();
  const { axBe } = useAxiosInterceptor();
  const { setToken, setUser, setRefreshtoken } = useAuth();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [authError, setAuthError] = useState("");

  const isInvalidCredentialsError = (error: any) => {
    const message = String(error?.message ?? "").toLowerCase();
    const errorCode = error?.extensions?.errorCode;
    const authFailure = error?.extensions?.authFailure;
    return (
      errorCode === 401 ||
      authFailure === "INVALID_CREDENTIALS" ||
      message.includes("invalid email or password") ||
      message.includes("bad credentials")
    );
  };

  const getLoginErrorMessage = (error: any) =>
    isInvalidCredentialsError(error)
      ? INVALID_CREDENTIALS_MESSAGE
      : GENERIC_LOGIN_MESSAGE;

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .required("Email is required"),
      password: Yup.string().max(255).required("Password is required"),
    }),
    onSubmit: async () => {
      setAuthError("");
      const variables = {
        storeUserSignInInput: {
          ...formik.values,
        },
      };
      if (!GRAPHQL_ENDPOINT) {
        console.error("GraphQL endpoint is not defined.");
        return;
      }
      try {
        setInProgress(true);
        const requestConfig: any = {
          meta: {
            suppressAuthErrorToast: true,
            skipAuthRefresh: true,
          },
        };
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: SIGNIN_STORE_USER,
          variables: variables,
        }, requestConfig);
        const loginError = data?.errors?.[0];
        if (loginError) {
          setAuthError(getLoginErrorMessage(loginError));
          setInProgress(false);
          return;
        }
        const token = data?.data?.storeUserSignIn?.accessToken;
        const refreshToken = data?.data?.storeUserSignIn?.refreshToken;
        const userRole = data?.data?.storeUserSignIn?.roles?.[0];
        const user = data?.data?.storeUserSignIn;
        const availableFeatureNames = data?.data?.storeUserSignIn?.availableFeatureNames;
        if (Array.isArray(availableFeatureNames) && availableFeatureNames.length > 0) {
          setCachedFeatureNames(availableFeatureNames);
        }
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("token", token);
        localStorage.setItem("role", userRole);
        localStorage.setItem("user", JSON.stringify(user));
        setRefreshtoken(refreshToken);
        setToken(token);
        setUser(user);
        navigate("/");
        setInProgress(false);
      } catch (error) {
        setAuthError(GENERIC_LOGIN_MESSAGE);
        setInProgress(false);
      }
    },
  });

  const togglePassword = () => {
    setPasswordVisible(!passwordVisible);
  };

  const isButtonDisabled = !formik.isValid || !formik.dirty;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: "linear-gradient(180deg, #F7F8FA 0%, #EEF1F4 100%)" }}>
      <div
        onClick={() => navigate("/")}
        className="flex items-center justify-center gap-x-2 cursor-pointer mb-8"
      >
        <Logo />
        <h1 className="text-2xl font-semibold text-[#111827]">EatsBits</h1>
      </div>

      <div className="w-full max-w-[420px] rounded-[14px] p-8 bg-white shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
        <h2 className="text-[28px] font-bold text-[#111827]" style={{ letterSpacing: "-0.01em" }}>
          Sign in to your dashboard
        </h2>
        <p className="text-[15px] text-[#6B7280] mt-1.5 mb-6">Manage orders, menus, and analytics</p>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-1.5">
              Email
            </label>
            <div
              className={`relative flex items-center h-[46px] pl-[44px] pr-4 rounded-[10px] border transition-all duration-150 ${
                formik.touched.email && formik.errors.email
                  ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
                  : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
              }`}
            >
              <span className="absolute left-4 top-1/2 -translate-y-1/2">
                <MailIcon />
              </span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-500 text-xs mt-1.5">{formik.errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#374151] mb-1.5">
              Password
            </label>
            <div
              className={`relative flex items-center h-[46px] pl-[44px] pr-12 rounded-[10px] border transition-all duration-150 ${
                formik.touched.password && formik.errors.password
                  ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
                  : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
              }`}
            >
              <span className="absolute left-4 top-1/2 -translate-y-1/2">
                <LockIcon />
              </span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type={passwordVisible ? "text" : "password"}
                id="password"
                name="password"
                placeholder="••••••••"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.password}
              />
              <button
                type="button"
                onClick={togglePassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                aria-label={passwordVisible ? "Hide password" : "Show password"}
              >
                <img
                  src={passwordVisible ? eyeIconVisible : eyeIcon}
                  alt=""
                  className="w-5 h-5"
                />
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="text-red-500 text-xs mt-1.5">{formik.errors.password}</p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <span className="relative flex h-[18px] w-[18px] shrink-0 flex-shrink-0 items-center justify-center self-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  className="peer sr-only"
                />
                <span className="absolute inset-0 rounded-[4px] border border-[#D1D5DB] bg-white transition-colors duration-150 peer-checked:border-[#FF6A3D] peer-checked:bg-[#FF6A3D]" />
                <svg className="relative z-10 h-3 w-3 text-white pointer-events-none opacity-0 transition-opacity peer-checked:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-[#4B5563] leading-none">
                Keep me signed in
              </span>
            </label>
            <Link
              to="/forgot-password"
              className="text-sm text-[#6B7280] hover:text-[#FF6A3D] transition-colors duration-150"
            >
              Forgot password?
            </Link>
          </div>

          <div>
            {inProgress ? (
              <div className="flex justify-center py-3">
                <BtnLoader />
              </div>
            ) : (
              <>
                {authError && (
                  <p className="mb-3 rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]">
                    {authError}
                  </p>
                )}
              <button
                type="submit"
                disabled={isButtonDisabled}
                className={`w-full h-12 rounded-[10px] font-semibold text-[15px] text-white transition-all duration-150 ${
                  isButtonDisabled
                    ? "bg-[#FD7657] opacity-50 cursor-not-allowed"
                    : "bg-[#FF6A3D] hover:-translate-y-px hover:bg-[#E85C32] active:translate-y-0"
                }`}
              >
                Sign in
              </button>
              </>
            )}
            <p className="text-center text-xs text-[#9CA3AF] mt-3">
              Secure access to your restaurant dashboard
            </p>
          </div>

          <p className="text-center text-sm text-[#6B7280] mt-7">
            Don&apos;t have an account?{" "}
            <Link to="/sign-up" className="font-semibold text-[#111827] hover:text-[#FF6A3D] transition-colors">
              Create account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};
